/**
 * Globaler, batch-koaleszierender Save-Mechanismus für Aufmaß-Daten.
 *
 * Hintergrund: Pro Foto rendert die Seite eine eigene `<AreaMeasurement>`-Instanz
 * mit eigenem Pro-Instanz-Debouncer. Bei 10 Fotos und schnellen Eingaben
 * entstanden 23+ `saveProject`-Calls in 6 Sekunden. Dieses Modul koalesziert
 * pro photoId — der jüngste Wert gewinnt — und führt maximal einen Flush
 * pro 500 ms global aus.
 *
 * Feature-Flag `USE_GLOBAL_MEASUREMENT_DEBOUNCER` (Default `true`):
 *   - true  → Patches durchlaufen die Queue (gewünschtes Verhalten).
 *   - false → Notrücksetzpfad: direkter Save, falls die Queue Probleme macht.
 *             Dann verhalten sich Saves wieder wie vor dem Patch.
 */

import { LocalStorageManager, type StoredMeasurementEntry } from './storage';

export const USE_GLOBAL_MEASUREMENT_DEBOUNCER = true;

const FLUSH_INTERVAL_MS = 500;

interface PendingItem {
  projectId: string;
  entry: StoredMeasurementEntry;
}

const pending = new Map<string, PendingItem>();
let timer: ReturnType<typeof setTimeout> | null = null;

function key(projectId: string, photoId: string): string {
  return `${projectId}::${photoId}`;
}

function flush(): void {
  timer = null;
  if (pending.size === 0) return;
  const batch = Array.from(pending.values());
  pending.clear();
  for (const { projectId, entry } of batch) {
    LocalStorageManager.saveMeasurementData(projectId, entry);
  }
}

/**
 * Reiht einen Messdaten-Patch in die Save-Queue ein. Mehrere Aufrufe für
 * dieselbe `photoId` werden überschrieben (overwrite-coalesce) — nur der
 * zuletzt eingegangene Wert wird geschrieben.
 */
export function enqueueMeasurementSave(
  projectId: string,
  entry: StoredMeasurementEntry,
): void {
  if (!USE_GLOBAL_MEASUREMENT_DEBOUNCER) {
    LocalStorageManager.saveMeasurementData(projectId, entry);
    return;
  }
  pending.set(key(projectId, entry.photoId), { projectId, entry });
  if (!timer) timer = setTimeout(flush, FLUSH_INTERVAL_MS);
}

/**
 * Synchroner Flush — für Unmount-, beforeunload- oder visibilitychange-Pfade.
 * Schreibt alle ausstehenden Patches sofort und löscht den Timer.
 */
export function flushMeasurementSavesSync(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  flush();
}

/**
 * Entfernt alle ausstehenden Patches für eine bestimmte photoId — wird
 * beim expliziten Foto-Löschen verwendet, damit kein Patch mehr für ein
 * gelöschtes Foto in der Queue verweilt.
 */
export function dropPendingMeasurementsForPhoto(
  projectId: string,
  photoId: string,
): void {
  pending.delete(key(projectId, photoId));
}
